import type { IProspect } from "@commonTypes/prospect"
import { baseServiceRequest } from "../baseServiceRequest"
import type {
  IAddProspectNote,
  IProspectNote,
  IAddProspectContact,
  IProspectContact,
  ISetProspectPhoneNumberRequest,
  IProspectPhone,
  IDeleteProspectPhoneNumberRequest,
  ISetProspectContactPreferencesRequest,
  ISendProspectEventRequest,
  IProspectAccount,
  IProspectAddressRequest,
  IProspectRequest,
  IProspectPartialOrder,
  IProspectSearch,
  IMemberSearchParams,
  ISaveAddressResponse,
} from "./types"
import type { IBaseEvent } from "@commonTypes/events"
import type { IShopper } from "@apis/shoppers/types"
import type { IAccountSearch } from "@apis/accounts/types"

export const addProspectNote = async ({
  prospect_guid,
  data,
}: {
  prospect_guid: string
  data: IAddProspectNote
}): Promise<IProspectNote> => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/prospects/prospect/${prospect_guid}/notes`,
    data: data,
  })
  return response.data
}

export const addProspectContact = async (
  data: IAddProspectContact,
): Promise<IProspectContact> => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/contacts/contact/offline`,
    data: data,
  })
  return response.data
}

export const setProspectContactPreferences = async ({
  prospect_guid,
  request,
}: {
  prospect_guid: string
  request: ISetProspectContactPreferencesRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/prospects/prospect/${prospect_guid}/contact_preferences`,
    data: request,
  })
  return response.data
}

export const getProspect = async (
  prospect_guid: string,
): Promise<IProspect> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/prospects/prospect/${prospect_guid}`,
  })
  return response.data
}

export const getProspectAccounts = async (
  prospect_guid: string,
): Promise<IProspectAccount[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/prospects/prospect/${prospect_guid}/accounts`,
  })
  return response.data
}

export const getProspectPhoneNumbers = async (
  prospect_guid: string,
): Promise<IProspectPhone[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/prospects/prospect/${prospect_guid}/phones`,
  })
  return response.data
}

export const setProspectPhoneNumber = async ({
  prospect_guid,
  request,
}: {
  prospect_guid: string
  request: ISetProspectPhoneNumberRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/prospects/prospect/${prospect_guid}/phones/new`,
    data: request,
  })
  return response.data
}

export const deleteProspectPhoneNumber = async ({
  prospect_guid,
  request,
}: {
  prospect_guid: string
  request: IDeleteProspectPhoneNumberRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/prospects/prospect/${prospect_guid}/phones/delete`,
    data: request,
  })
  return response.data
}

export const sendProspectEvent = async ({
  prospect_guid,
  request,
}: {
  prospect_guid: string
  request: ISendProspectEventRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/prospects/${prospect_guid}/event`,
    data: request,
  })
  return response.data
}

export const setProspectAddress = async ({
  prospect_guid,
  request,
}: {
  prospect_guid: string
  request: IProspectAddressRequest
}): Promise<ISaveAddressResponse> => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/prospects/prospect/${prospect_guid}/address`,
    data: request,
  })
  return response.data
}

export const updateProspect = async ({
  prospect_guid,
  request,
}: {
  prospect_guid: string
  request: IProspectRequest
}) => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/prospects/prospect/${prospect_guid}`,
    data: request,
  })
  return response.data
}

export const createProspect = async ({
  request,
}: {
  request: IProspectRequest
}): Promise<IProspect> => {
  const response = await baseServiceRequest({
    method: "POST",
    url: `/prospects/prospect`,
    data: request,
  })
  return response.data
}

export const getProspectEvents = async (
  prospect_guid: string,
): Promise<IBaseEvent[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `prospects/prospect/${prospect_guid}/activities`,
  })
  return response.data
}

export const getProspectPartialOrders = async (
  prospect_guid: string,
): Promise<IProspectPartialOrder[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/prospects/prospect/${prospect_guid}/partials/orders`,
  })
  return response.data
}

export const getProspectPartialShoppers = async (
  prospect_guid: string,
): Promise<Pick<IShopper, "initial_sale_dt" | "shopper_guid">[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/prospects/prospect/${prospect_guid}/partials/shoppers`,
  })
  return response.data
}

export const getProspectShoppers = async (
  prospect_guid: string,
): Promise<IShopper[]> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: `/prospects/prospect/${prospect_guid}/shoppers`,
  })
  return response.data
}

export const getProspects = async ({
  params,
  signal,
}: {
  params: IMemberSearchParams
  signal: AbortSignal
}): Promise<IProspectSearch> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: "/prospects/prospect/search",
    params,
    signal,
  })
  return response.data
}

export const getAccounts = async ({
  params,
  signal,
}: {
  params: IMemberSearchParams
  signal: AbortSignal
}): Promise<IAccountSearch> => {
  const response = await baseServiceRequest({
    method: "GET",
    url: "/prospects/prospect/accounts/search",
    params,
    signal,
  })
  return response.data
}
